import { ClampToEdgeWrapping, Color, DataTexture, Group, LinearFilter, LinearMipmapLinearFilter, Mesh, MeshPhysicalMaterial, NoColorSpace, Quaternion, RGBAFormat, RepeatWrapping, SphereGeometry, UnsignedByteType, Vector2, Vector3 } from 'three';
import type { Texture } from 'three';
import { paintPearlMaps } from '../painter/maps';

export const PEARL_RADIUS = 0.62;

export type PearlMode = 'idle' | 'roll' | 'dive' | 'air' | 'crash' | 'fall';

export interface PearlDrive {
  mode: PearlMode;
  speed: number;
  /** 0..1 flow (combo / momentum) — a faint inner warmth. */
  flow: number;
  time: number;
  /** World velocity (m/s). */
  velocity: Vector3;
  /** Ground normal while rolling. */
  normal: Vector3;
}

function mapTexture(data: Uint8Array, w: number, h: number): DataTexture {
  const t = new DataTexture(data, w, h, RGBAFormat, UnsignedByteType);
  t.wrapS = RepeatWrapping;
  t.wrapT = ClampToEdgeWrapping;
  t.minFilter = LinearMipmapLinearFilter;
  t.magFilter = LinearFilter;
  t.generateMipmaps = true;
  t.anisotropy = 4;
  t.colorSpace = NoColorSpace;
  t.needsUpdate = true;
  return t;
}

const _axis = new Vector3();
const _tmpC = new Color();
const _dq = new Quaternion();
const _h = new Vector3();

/**
 * The Cloud Pearl: a white, clear-coated pearl. Physically based, so it simply mirrors its
 * world — the host feeds it a live environment probe (sky, sand, meadow). Small imperfections
 * (dimples, orange peel, faint out-of-roundness, smudged gloss) live in normal/roughness maps
 * and roll with it, so you can read its spin. Animation is pure maths: roll from velocity,
 * squash on landings, a slight flattening while diving.
 * Origin at the contact point; the sphere centre sits at y = PEARL_RADIUS.
 */
export class PearlRig {
  readonly root = new Group();
  readonly pivot = new Group();
  readonly shell: Mesh;
  readonly material: MeshPhysicalMaterial;
  private readonly maps: Texture[];
  private readonly roll = new Quaternion();
  private squash = 0;
  private squashV = 0;
  private glow = 0;

  constructor() {
    const m = paintPearlMaps(384, 192, 5);
    const normal = mapTexture(m.normal, 384, 192);
    const rough = mapTexture(m.roughness, 384, 192);
    this.maps = [normal, rough];
    this.material = new MeshPhysicalMaterial({
      name: 'pearl',
      color: new Color('#fdfaf6'),
      // Base layer: soft, satiny white (roughness map × 2.6 ≈ 0.16–0.7 with smudges)…
      roughness: 2.6,
      roughnessMap: rough,
      metalness: 0,
      normalMap: normal,
      normalScale: new Vector2(1.7, 1.7),
      // …under a crisp clear coat that mirrors the world, rippled by the tiny imperfections.
      clearcoat: 0.7,
      clearcoatRoughness: 0.045,
      clearcoatNormalMap: normal,
      clearcoatNormalScale: new Vector2(1.1, 1.1),
      // A whisper of nacre at grazing angles only.
      iridescence: 0.12,
      iridescenceIOR: 1.4,
      iridescenceThicknessRange: [300, 480],
      sheen: 0.35,
      sheenRoughness: 0.45,
      sheenColor: new Color('#fff3f0'),
      specularIntensity: 0.75,
      envMapIntensity: 1.0,
      emissive: new Color('#fff6ee'),
      emissiveIntensity: 0,
    });
    this.shell = new Mesh(new SphereGeometry(PEARL_RADIUS, 72, 48), this.material);
    this.shell.castShadow = true;
    this.shell.receiveShadow = true;
    this.shell.name = 'pearl-shell';
    this.pivot.position.y = PEARL_RADIUS;
    this.pivot.add(this.shell);
    this.root.add(this.pivot);
  }

  /**
   * The pearl's colour. Changes glide over ~0.6 s (the shell, its sheen and its inner glow all
   * follow); `instant` snaps (a freshly built pearl).
   */
  tint(hex: string, instant = false): void {
    this.target.set(hex);
    if (instant) this.applyTint(1);
  }

  private readonly target = new Color('#fdfaf6');
  private readonly white = new Color('#ffffff');

  private applyTint(k: number): void {
    const m = this.material;
    m.color.lerp(this.target, k);
    m.sheenColor.lerp(_tmpC.copy(this.target).lerp(this.white, 0.4), k);
    m.emissive.lerp(_tmpC.copy(this.target).lerp(this.white, 0.5), k);
  }

  setEnvMap(env: Texture | null): void {
    if (this.material.envMap === env) return;
    this.material.envMap = env;
    this.material.needsUpdate = true;
  }

  /** World-space centre of the pearl (for environment probes). */
  center(out: Vector3): Vector3 {
    return this.pivot.getWorldPosition(out);
  }

  /** Landing / bump: compress the pearl, scaled by impact (0..1+). */
  impulse(k: number): void {
    this.squashV -= k * 9;
  }

  update(d: PearlDrive, dt: number): void {
    // Roll: ω = n × v / r (rolling without slipping).
    if (d.mode === 'roll' || d.mode === 'dive' || d.mode === 'idle') {
      _axis.crossVectors(d.normal, d.velocity);
      const w = _axis.length() / PEARL_RADIUS;
      if (w > 1e-4) {
        _dq.setFromAxisAngle(_axis.normalize(), w * dt);
        this.roll.premultiply(_dq);
      }
    } else {
      // Airborne: keep spinning about the last horizontal axis, slowly.
      _h.set(d.velocity.x, 0, d.velocity.z);
      _axis.set(0, 1, 0).cross(_h);
      const w = _axis.length() / PEARL_RADIUS;
      if (w > 1e-4) {
        _dq.setFromAxisAngle(_axis.normalize(), w * dt * 0.6);
        this.roll.premultiply(_dq);
      }
    }
    this.shell.quaternion.copy(this.roll);

    // Squash spring.
    const k = 140;
    const damp = 12;
    this.squashV += (-k * this.squash - damp * this.squashV) * dt;
    this.squash += this.squashV * dt;
    const sq = Math.max(-0.3, Math.min(0.25, this.squash));
    const dive = d.mode === 'dive' ? 0.05 : 0;
    const sy = 1 + sq - dive;
    const sxz = 1 / Math.sqrt(Math.max(0.4, sy));
    this.pivot.scale.set(sxz, sy, sxz);
    this.pivot.position.y = PEARL_RADIUS * sy;

    if (!this.material.color.equals(this.target)) this.applyTint(1 - Math.exp(-dt * 6));

    // Flow: the faintest inner warmth, never a glow-stick.
    // A pearl's lustre: it always reads a little luminous.
    const target = 0.09 + d.flow * 0.1;
    this.glow += (target - this.glow) * Math.min(1, dt * 4);
    this.material.emissiveIntensity = this.glow;
  }

  reset(): void {
    this.roll.identity();
    this.squash = 0;
    this.squashV = 0;
    this.glow = 0;
  }

  dispose(): void {
    this.shell.geometry.dispose();
    this.material.dispose();
    for (const t of this.maps) t.dispose();
  }
}
